<!--
  Add/remove/validate a list of email addresses or WhatsApp numbers, shown as
  closable chips. Extracted out of DashboardDigestSettings.vue (KAN-32) --
  that component needed this exact block once for email + once for
  WhatsApp; ReportDigestSettings.vue needs it 8 times (email + WhatsApp,
  times 4 report types), so it earned a real component instead of staying
  copy-pasted. Validation/normalization logic is unchanged from where it
  came from.
-->
<template>
  <div class="rci">
    <div v-if="modelValue.length" class="rci__chips">
      <v-chip
        v-for="entry in modelValue"
        :key="entry"
        size="small"
        closable
        :disabled="disabled"
        @click:close="remove(entry)"
      >
        {{ entry }}
      </v-chip>
    </div>
    <p v-else class="rci__empty">{{ emptyText }}</p>

    <div class="rci__add-row">
      <v-text-field
        v-model="draft"
        :placeholder="kind === 'email' ? 'boss@company.com' : '+919876543210'"
        variant="outlined"
        density="compact"
        hide-details
        :disabled="disabled"
        class="rci__add-input"
        @keydown.enter.prevent="add"
      />
      <v-btn
        variant="flat"
        color="primary"
        size="small"
        class="text-none"
        :disabled="disabled || !draft.trim()"
        @click="add"
      >
        Add
      </v-btn>
    </div>
    <p v-if="error" class="rci__error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue'

defineOptions({ name: 'RecipientChipInput' })

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  kind: { type: String, required: true, validator: (v) => ['email', 'whatsapp'].includes(v) },
  disabled: { type: Boolean, default: false },
  emptyText: { type: String, default: 'No recipients yet -- add one below.' },
})

const emit = defineEmits(['update:modelValue'])

const draft = ref('')
const error = ref('')

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function add() {
  error.value = ''
  const raw = draft.value.trim()
  if (!raw) return

  if (props.kind === 'email') {
    const value = raw.toLowerCase()
    if (!EMAIL_RE.test(value)) {
      error.value = 'Enter a valid email address.'
      return
    }
    if (props.modelValue.includes(value)) { draft.value = ''; return }
    emit('update:modelValue', [...props.modelValue, value])
  } else {
    const digits = raw.replace(/[^\d+]/g, '')
    // Loose E.164-ish check -- the actual send already requires a valid
    // WhatsApp-reachable number, this just catches an obvious typo before
    // it's saved.
    if (!/^\+?\d{8,15}$/.test(digits)) {
      error.value = 'Enter a valid phone number, with country code.'
      return
    }
    const normalized = digits.startsWith('+') ? digits : `+${digits}`
    if (props.modelValue.includes(normalized)) { draft.value = ''; return }
    emit('update:modelValue', [...props.modelValue, normalized])
  }
  draft.value = ''
}

function remove(entry) {
  emit('update:modelValue', props.modelValue.filter((e) => e !== entry))
}
</script>

<style scoped>
.rci {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rci__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.rci__empty {
  font-size: 0.8rem;
  opacity: 0.6;
  margin: 0;
}

.rci__add-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.rci__add-input {
  flex: 1;
}

.rci__error {
  font-size: 0.76rem;
  color: #ef4444;
  margin: 0;
}
</style>
